import { DATE_FORMAT } from '@components/dashboards/components/DateRangePicker.utils';
import { EcoCard, EcoIcon, EcoTable, EcoText } from '@components/shared';
import EcoEmptyState from '@components/shared/EcoEmptyState';
import { EcoPageTitle } from '@components/shared/EcoPageTitle';
import { ColumnConfig } from '@components/shared/EcoTable.utils';
import EcoScrollView from '@components/views/EcoScrollView';
import { REDEMPTION_NAMES_MAP, RewardRedemptionResponse } from '@ecocart/entities';
import { formatNumber } from '@ecocart/universal-utils';
import { useGlobal } from '@hooks/useGlobal';
import { useRewards } from '@hooks/useRewards';
import { BackLink } from '@navigation/BackLink';
import { Gap } from '@utils/layout';
import { format, parseISO } from 'date-fns';
import { lowerCase, startCase } from 'lodash';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { GlobalScreenProps } from '../navigation/types';

export default function RewardsHistoryScreen({}: GlobalScreenProps): JSX.Element {
  const { session } = useGlobal();
  const shopName = session?.merchantAdmin?.shopName || '';
  const { data, isFetching } = useRewards(shopName);

  const getSubmittedByString = (value: RewardRedemptionResponse['redemptions'][0]) => {
    return (
      <View>
        <EcoText>{`${value.created_by.first_name} ${value.created_by.last_name} (${value.created_by.id})`}</EcoText>
        <EcoText color="subdued" fontSize="sm">
          {value.identifier}
        </EcoText>
      </View>
    );
  };

  const columnConfig: ColumnConfig<RewardRedemptionResponse['redemptions'][0]>[] = useMemo(
    () => [
      {
        field: 'amount_usd',
        title: 'Amount (USD)',
        flex: 1.5,
        searchable: true,
        align: 'center',
        format: (value) => formatNumber(value.amount_usd, { style: 'currency', currency: 'USD' })
      },
      {
        field: 'created_by',
        title: 'Submitted By / Request ID',
        flex: 3,
        searchable: true,
        format: getSubmittedByString
      },
      {
        field: 'created_at',
        title: 'Submitted On',
        searchable: true,
        format: (value) => format(parseISO(String(value.created_at)), DATE_FORMAT)
      },
      {
        field: 'redemption_type',
        title: 'Category',
        flex: 2,
        searchable: true,
        format: (value) => startCase(lowerCase(REDEMPTION_NAMES_MAP[value.redemption_type])).replace('Eco Cart', 'EcoCart') || 'Unknown'
      }
    ],
    []
  );

  return (
    <EcoScrollView>
      <View className="flex-row">
        <BackLink href="/rewards" label="Back To Rewards" />
      </View>

      <EcoPageTitle title="History" />
      {isFetching || (data && data.redemptions.length > 0) ? (
        <EcoCard>
          <EcoText fontSize="lg" className="mb-6">
            View all your rewards related history here
          </EcoText>
          <EcoTable
            defaultDataQuery={{ sorts: [{ field: 'created_at', dir: 'desc' }] }}
            data={data?.redemptions || []}
            isLoading={!data}
            columns={columnConfig}
          />
        </EcoCard>
      ) : (
        <EcoCard>
          <View className="items-center justify-center py-9" style={Gap(4)}>
            <EcoIcon name="warning" className="text-warning-500" size="6xl" />
            <EcoEmptyState title="No Rewards History Found" />
          </View>
        </EcoCard>
      )}
    </EcoScrollView>
  );
}
